import type {
  GeoJsonFeature,
  GeoJsonFeatureCollection,
  GeoJsonGeometry,
  GeoJsonLineString,
  GeoJsonMultiLineString,
  GeoJsonMultiPoint,
  GeoJsonPoint,
  GeoJsonPolygon,
} from '../geojsonBase';

export const CRACKMAP_WFS_BASE_URL =
  'https://geoserver.xais.co.uk/geoserver/wfs_layers/ows';

export const CRACKMAP_WFS_BBOX_SRS = 'EPSG:3857';

export const CRACKMAP_WFS_OUTPUT_SRS = 'EPSG:4326';

export const CRACKMAP_WFS_PROPERTY_NAMES =
  'geometry,sectionID,crackID,length,depth,width';

export const CRACKMAP_WFS_FORMAT_OPTIONS = 'decimals:5';

export interface MapExtentBbox {
  readonly xmin: number;
  readonly ymin: number;
  readonly xmax: number;
  readonly ymax: number;
}

export interface CrackmapWfsFetchResult {
  readonly collection: GeoJsonFeatureCollection;
  readonly totalFeatures: number | null;
  readonly returnedFeatures: number;
  readonly truncated: boolean;
}

export interface GeoserverServiceException {
  readonly code: string | null;
  readonly message: string;
}

export class CrackmapWfsError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = 'CrackmapWfsError';
    this.code = code;
  }
}

export function isGeoJsonFeatureCollection(value: unknown): value is GeoJsonFeatureCollection {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { type?: unknown; features?: unknown };
  return candidate.type === 'FeatureCollection' && Array.isArray(candidate.features);
}

/**
 * GeoServer often returns HTTP 200 with an OGC ServiceExceptionReport XML body
 * instead of GeoJSON when parameters are invalid.
 */
export function parseGeoserverServiceException(
  body: string,
): GeoserverServiceException | null {
  const trimmed = body.trim();
  if (!trimmed || !/ServiceExceptionReport/i.test(trimmed)) {
    return null;
  }

  const codeMatch = trimmed.match(
    /<ServiceException\b[^>]*\bcode\s*=\s*["']([^"']+)["'][^>]*>/i,
  );
  const messageMatch = trimmed.match(
    /<ServiceException\b[^>]*>([\s\S]*?)<\/ServiceException>/i,
  );
  const rawMessage = messageMatch?.[1]?.replace(/<[^>]+>/g, '').trim() ?? '';
  const message = rawMessage.replace(/\s+/g, ' ').trim();
  if (!message) {
    return {
      code: codeMatch?.[1] ?? null,
      message: 'GeoServer rejected the crackmap WFS request',
    };
  }

  return {
    code: codeMatch?.[1] ?? null,
    message,
  };
}

export function getCrackmapWfsErrorMessage(error: unknown): string {
  if (error instanceof CrackmapWfsError) {
    return error.message;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Crackmap WFS request failed';
}

function readOptionalFeatureCount(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function readWfsFeatureCounts(payload: unknown): {
  readonly totalFeatures: number | null;
  readonly returnedFeatures: number;
} {
  if (typeof payload !== 'object' || payload === null) {
    return { totalFeatures: null, returnedFeatures: 0 };
  }

  const candidate = payload as Record<string, unknown>;
  const features = candidate.features;
  const returnedFeatures = Array.isArray(features) ? features.length : 0;
  const totalFeatures =
    readOptionalFeatureCount(candidate.totalFeatures) ??
    readOptionalFeatureCount(candidate.numberMatched) ??
    readOptionalFeatureCount(candidate.numberOfFeatures);

  return { totalFeatures, returnedFeatures };
}

export function buildCrackmapWfsUrl(
  wfsUrlOrTypeName: string,
  extent: MapExtentBbox,
  propertyNames: string = CRACKMAP_WFS_PROPERTY_NAMES,
): string {
  const trimmed = wfsUrlOrTypeName.trim();
  const isFullUrl = /^https?:\/\//i.test(trimmed);

  const url = isFullUrl
    ? new URL(trimmed)
    : new URL(CRACKMAP_WFS_BASE_URL);

  if (!isFullUrl) {
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '1.0.0');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('typeName', trimmed);
  } else {
    if (!url.searchParams.get('service')) {
      url.searchParams.set('service', 'WFS');
    }
    if (!url.searchParams.get('version')) {
      url.searchParams.set('version', '1.0.0');
    }
    if (!url.searchParams.get('request')) {
      url.searchParams.set('request', 'GetFeature');
    }
  }

  url.searchParams.set('outputFormat', 'application/json');
  url.searchParams.set('srsname', CRACKMAP_WFS_OUTPUT_SRS);
  url.searchParams.set('propertyName', propertyNames);
  url.searchParams.set('format_options', CRACKMAP_WFS_FORMAT_OPTIONS);
  url.searchParams.set(
    'bbox',
    `${extent.xmin},${extent.ymin},${extent.xmax},${extent.ymax},${CRACKMAP_WFS_BBOX_SRS}`,
  );

  return url.toString();
}

export function buildCrackmapBboxCacheKey(extent: MapExtentBbox): string {
  const round = (value: number): number => Math.round(value);
  return [
    round(extent.xmin),
    round(extent.ymin),
    round(extent.xmax),
    round(extent.ymax),
  ].join(',');
}

/**
 * Web Mercator metres-per-pixel at zoom 0 (256px tiles).
 * Used to synthesise a street-level bbox while the map is still zoomed out.
 */
export const WEB_MERCATOR_METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392804097;

/**
 * Builds a street-scale Web Mercator extent around a map centre so WFS can be
 * prefetched before the user reaches street zoom (without querying the huge
 * zoomed-out viewport).
 */
export function buildCrackmapStreetLevelPrefetchExtent(
  centerX: number,
  centerY: number,
  mapWidthPx: number,
  mapHeightPx: number,
  zoom: number,
): MapExtentBbox {
  const safeZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 18;
  const metersPerPixel = WEB_MERCATOR_METERS_PER_PIXEL_AT_ZOOM_0 / 2 ** safeZoom;
  const widthPx = Math.max(mapWidthPx, 640);
  const heightPx = Math.max(mapHeightPx, 480);
  // Slightly larger than the viewport so small pans are still cache hits.
  const pad = 1.15;
  const halfW = (widthPx * metersPerPixel * pad) / 2;
  const halfH = (heightPx * metersPerPixel * pad) / 2;
  return {
    xmin: centerX - halfW,
    ymin: centerY - halfH,
    xmax: centerX + halfW,
    ymax: centerY + halfH,
  };
}

export function parseCrackmapBboxCacheKey(cacheKey: string): MapExtentBbox | null {
  const parts = cacheKey.split(',').map((part) => Number(part));
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) {
    return null;
  }
  return {
    xmin: parts[0]!,
    ymin: parts[1]!,
    xmax: parts[2]!,
    ymax: parts[3]!,
  };
}

export function isMapExtentContained(
  inner: MapExtentBbox,
  outer: MapExtentBbox,
  tolerance?: number,
): boolean {
  const outerWidth = Math.abs(outer.xmax - outer.xmin);
  const outerHeight = Math.abs(outer.ymax - outer.ymin);
  const resolvedTolerance =
    tolerance ??
    Math.max(1, Math.min(outerWidth, outerHeight) * 0.05);

  return (
    inner.xmin >= outer.xmin - resolvedTolerance &&
    inner.ymin >= outer.ymin - resolvedTolerance &&
    inner.xmax <= outer.xmax + resolvedTolerance &&
    inner.ymax <= outer.ymax + resolvedTolerance
  );
}

export function isMapExtentApproximatelyEqual(
  left: MapExtentBbox,
  right: MapExtentBbox,
  relativeTolerance = 0.12,
): boolean {
  const width = Math.max(
    Math.abs(left.xmax - left.xmin),
    Math.abs(right.xmax - right.xmin),
    1,
  );
  const height = Math.max(
    Math.abs(left.ymax - left.ymin),
    Math.abs(right.ymax - right.ymin),
    1,
  );
  const tolX = width * relativeTolerance;
  const tolY = height * relativeTolerance;

  return (
    Math.abs(left.xmin - right.xmin) <= tolX &&
    Math.abs(left.ymin - right.ymin) <= tolY &&
    Math.abs(left.xmax - right.xmax) <= tolX &&
    Math.abs(left.ymax - right.ymax) <= tolY
  );
}

export interface ContainingCrackmapCacheHit {
  readonly cacheKey: string;
  readonly features: GeoJsonFeatureCollection;
  readonly extent: MapExtentBbox;
}

/**
 * Finds the smallest cached extent that already covers `extent`.
 * Used to skip WFS when zooming/panning inside data we already have.
 */
export function findContainingCachedCrackmapFeatures(
  extent: MapExtentBbox,
  extentByCacheKey: ReadonlyMap<string, MapExtentBbox>,
  featureCache: ReadonlyMap<string, GeoJsonFeatureCollection>,
): ContainingCrackmapCacheHit | null {
  let best: ContainingCrackmapCacheHit | null = null;
  let bestArea = Number.POSITIVE_INFINITY;

  for (const [cacheKey, cachedExtent] of extentByCacheKey.entries()) {
    if (!isMapExtentContained(extent, cachedExtent)) {
      continue;
    }
    const features = featureCache.get(cacheKey);
    if (!features) {
      continue;
    }
    const area =
      (cachedExtent.xmax - cachedExtent.xmin) * (cachedExtent.ymax - cachedExtent.ymin);
    if (area < bestArea) {
      bestArea = area;
      best = { cacheKey, features, extent: cachedExtent };
    }
  }

  return best;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isLineString(geometry: GeoJsonGeometry): geometry is GeoJsonLineString {
  return geometry.type === 'LineString';
}

function isMultiLineString(geometry: GeoJsonGeometry): geometry is GeoJsonMultiLineString {
  return geometry.type === 'MultiLineString';
}

function isPolygon(geometry: GeoJsonGeometry): geometry is GeoJsonPolygon {
  return geometry.type === 'Polygon';
}

function isPoint(geometry: GeoJsonGeometry): geometry is GeoJsonPoint {
  return geometry.type === 'Point';
}

function isMultiPoint(geometry: GeoJsonGeometry): geometry is GeoJsonMultiPoint {
  return geometry.type === 'MultiPoint';
}

function isValidPointCoordinates(coordinates: GeoJsonPoint['coordinates']): boolean {
  return (
    Array.isArray(coordinates) &&
    coordinates.length >= 2 &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number' &&
    Number.isFinite(coordinates[0]) &&
    Number.isFinite(coordinates[1])
  );
}

export function prepareCrackmapGeoJsonForArcGis(
  collection: GeoJsonFeatureCollection,
): GeoJsonFeatureCollection {
  const features: GeoJsonFeature[] = [];

  for (const feature of collection.features) {
    if (!feature || feature.type !== 'Feature' || !feature.geometry) {
      continue;
    }

    const properties = isRecord(feature.properties) ? feature.properties : {};
    const geometry = feature.geometry;

    if (isPoint(geometry)) {
      if (!isValidPointCoordinates(geometry.coordinates)) {
        continue;
      }
      features.push({
        type: 'Feature',
        id: features.length + 1,
        geometry: {
          type: 'Point',
          coordinates: geometry.coordinates,
        },
        properties,
      });
      continue;
    }

    if (isMultiPoint(geometry)) {
      for (const pointCoordinates of geometry.coordinates) {
        if (!isValidPointCoordinates(pointCoordinates)) {
          continue;
        }
        features.push({
          type: 'Feature',
          id: features.length + 1,
          geometry: {
            type: 'Point',
            coordinates: pointCoordinates,
          },
          properties,
        });
      }
      continue;
    }

    if (isLineString(geometry)) {
      features.push({
        type: 'Feature',
        id: features.length + 1,
        geometry: {
          type: 'LineString',
          coordinates: geometry.coordinates,
        },
        properties,
      });
      continue;
    }

    if (isMultiLineString(geometry)) {
      for (const lineCoordinates of geometry.coordinates) {
        if (!Array.isArray(lineCoordinates) || lineCoordinates.length < 2) {
          continue;
        }
        features.push({
          type: 'Feature',
          id: features.length + 1,
          geometry: {
            type: 'LineString',
            coordinates: lineCoordinates,
          },
          properties,
        });
      }
      continue;
    }

    if (isPolygon(geometry)) {
      features.push({
        type: 'Feature',
        id: features.length + 1,
        geometry: {
          type: 'Polygon',
          coordinates: geometry.coordinates,
        },
        properties,
      });
    }
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export async function fetchCrackmapWfsFeatures(
  wfsUrlOrTypeName: string,
  extent: MapExtentBbox,
  signal?: AbortSignal,
  propertyNames: string = CRACKMAP_WFS_PROPERTY_NAMES,
): Promise<CrackmapWfsFetchResult> {
  const url = buildCrackmapWfsUrl(wfsUrlOrTypeName, extent, propertyNames);

  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw new CrackmapWfsError(
      error instanceof Error && error.message.trim()
        ? `Crackmap WFS network error: ${error.message}`
        : 'Crackmap WFS network error',
      'NetworkError',
    );
  }

  let bodyText: string;
  try {
    bodyText = await response.text();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw new CrackmapWfsError(
      'Crackmap WFS response body could not be read',
      'NetworkError',
    );
  }

  const serviceException = parseGeoserverServiceException(bodyText);
  if (serviceException) {
    const prefix = serviceException.code
      ? `Crackmap WFS error (${serviceException.code})`
      : 'Crackmap WFS error';
    throw new CrackmapWfsError(
      `${prefix}: ${serviceException.message}`,
      serviceException.code,
    );
  }

  if (!response.ok) {
    const statusLabel = response.statusText.trim()
      ? `${response.status} ${response.statusText.trim()}`
      : String(response.status);
    throw new CrackmapWfsError(
      `Crackmap WFS request failed (${statusLabel})`,
      String(response.status),
    );
  }

  if (!bodyText.trim()) {
    throw new CrackmapWfsError(
      'Crackmap WFS response was empty',
      'EmptyResponse',
    );
  }

  let payload: unknown;
  try {
    payload = JSON.parse(bodyText) as unknown;
  } catch {
    throw new CrackmapWfsError(
      'Crackmap WFS response was not valid JSON GeoJSON',
      'InvalidJson',
    );
  }

  if (!isGeoJsonFeatureCollection(payload)) {
    throw new CrackmapWfsError(
      'Crackmap WFS response was not a GeoJSON FeatureCollection',
      'InvalidGeoJson',
    );
  }

  const counts = readWfsFeatureCounts(payload);
  const collection = prepareCrackmapGeoJsonForArcGis(payload);
  const truncated =
    counts.totalFeatures !== null && counts.returnedFeatures < counts.totalFeatures;

  return {
    collection,
    totalFeatures: counts.totalFeatures,
    returnedFeatures: collection.features.length,
    truncated,
  };
}
